import React, { useState, useCallback, useMemo } from 'react';
import { connectionsApi } from '../services/api/connections';
import { Partner } from '../types/partner';
import { UpdateConnectionRequest, TransferMethod, HttpAuthType } from '../types/connection';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { PreCreationTestPanel } from './connections/PreCreationTestPanel';

interface EditConnectionProps {
  connectionId: string;
  partners: Partner[];
  onClose: () => void;
  onUpdate: () => void;
}

function EditConnection({ connectionId, partners, onClose, onUpdate }: EditConnectionProps) {
  // THUB-298: the connection is a QUERY; the form holds only the operator's EDITS over it.
  //
  // It was a useState pair filled by `loadConnection()` in a mount effect, which is the
  // set-state-in-effect the rule flags. Deriving the form values as `{...fromServer, ...edits}`
  // also fixes a real hazard the old shape had: `setFormData` seeded a snapshot of the server
  // config once, so anything that re-ran the load (a retry, a remount) replaced the operator's
  // in-progress edits wholesale.
  const connectionQuery = useQuery({
    queryKey: queryKeys.connections.detail(connectionId),
    queryFn: () => connectionsApi.getConnection(connectionId),
  });
  const connection = connectionQuery.data ?? null;

  /** Only the fields the operator has actually touched. */
  const [edits, setEdits] = useState<UpdateConnectionRequest>({});

  const serverForm: UpdateConnectionRequest = useMemo(
    () =>
      connection
        ? { name: connection.name, config: { ...connection.config }, version: connection.version }
        : {},
    [connection]
  );

  const formData: UpdateConnectionRequest = useMemo(
    () => ({
      ...serverForm,
      ...edits,
      // config is merged one level deeper -- an edit to a single field must not drop the rest of
      // the server config, which a shallow spread would.
      config: { ...(serverForm.config ?? {}), ...(edits.config ?? {}) },
    }),
    [serverForm, edits]
  );

  /** Same signature the existing handlers use. */
  const setFormData = useCallback(
    (updater: UpdateConnectionRequest | ((prev: UpdateConnectionRequest) => UpdateConnectionRequest)) => {
      setEdits((prev) => {
        const base = { ...serverForm, ...prev, config: { ...(serverForm.config ?? {}), ...(prev.config ?? {}) } };
        return typeof updater === 'function' ? updater(base) : updater;
      });
    },
    [serverForm]
  );
  // Loading comes from the query now, not a flag someone has to remember to clear.
  const loading = connectionQuery.isPending;
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // T026: HTTP-specific state for custom OAuth2 params
  // Derived the same way: the server's OAuth2 custom params until the operator edits them.
  const [customParamsOverride, setCustomParamsOverride] = useState<Record<string, string> | null>(null);
  const customParams: Record<string, string> =
    customParamsOverride ??
    ((connection?.config?.oauth2CustomParams as Record<string, string> | undefined) ?? {});
  const setCustomParams = setCustomParamsOverride as React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    try {
      // Validate form
      const validationErrors: Record<string, string> = {};

      if (!formData.name?.trim()) {
        validationErrors.name = 'Connection name is required';
      }

      // T026: HTTP/API validation
      const isHttp = connection?.transferMethod === 'api';
      if (isHttp) {
        if (!formData.config?.baseUrl?.trim()) {
          validationErrors.baseUrl = 'Base URL is required for HTTP connections';
        } else if (!formData.config.baseUrl.startsWith('https://')) {
          validationErrors.baseUrl = 'Base URL must use HTTPS';
        }

        const authType = formData.config?.httpAuthType || 'none';
        if (authType === 'oauth2_cc' || authType === 'oauth2_custom') {
          if (!formData.config?.oauth2ClientId?.trim()) {
            validationErrors.oauth2ClientId = 'Client ID is required for OAuth2';
          }
          if (!formData.config?.oauth2TokenEndpoint?.trim()) {
            validationErrors.oauth2TokenEndpoint = 'Token Endpoint is required for OAuth2';
          }
        }
      }

      // Hosted SFTP: validate SSH public key format if provided
      const isHostedSftp = connection?.hostingType === 'hosted' && connection?.transferMethod === 'sftp';
      if (isHostedSftp && formData.config?.public_key_content?.trim()) {
        const keyContent = formData.config.public_key_content.trim();
        const validPrefixes = ['ssh-rsa ', 'ssh-ed25519 ', 'ecdsa-sha2-nistp256 ', 'ecdsa-sha2-nistp384 ', 'ecdsa-sha2-nistp521 ', 'ssh-dss '];
        const hasValidPrefix = validPrefixes.some(prefix => keyContent.startsWith(prefix));
        if (!hasValidPrefix) {
          validationErrors.publicKeyContent = 'Invalid SSH public key format. Key must start with ssh-rsa, ssh-ed25519, or ecdsa-sha2-nistp*';
        }
      }

      // Remote SFTP/FTP need host/port/username. HTTP (api) and Azure Blob are also remote
      // but have no host/port -- scope this to the SFTP-family transports.
      const isRemote = connection?.hostingType === 'remote';
      const isRemoteSftpLike = isRemote && (connection?.transferMethod === 'sftp' || connection?.transferMethod === 'ftp');
      if (isRemoteSftpLike) {
        if (!formData.config?.host?.trim()) {
          validationErrors.host = 'Host is required for remote connections';
        }
        if (!formData.config?.port || formData.config.port < 1 || formData.config.port > 65535) {
          validationErrors.port = 'Valid port number is required';
        }
        if (!formData.config?.username?.trim()) {
          validationErrors.username = 'Username is required for remote connections';
        }
      }

      // THUB-220: Azure Blob edit validation. Container URL required + shape; SAS is
      // write-only (leave blank to keep the stored token, matching HTTP secret fields).
      if (connection?.transferMethod === 'azure_blob') {
        const url = formData.config?.azureContainerUrl?.trim();
        if (!url) {
          validationErrors.azureContainerUrl = 'Container URL is required';
        } else if (!/^https:\/\/[^/]+\.blob\.core\.windows\.net\/[^/]+/.test(url)
                   && !/^https:\/\/[^/]+\.cgic-internal\.com\/[^/]+/.test(url)) {
          // Also allow the internal DEV Azurite validation host (*.cgic-internal.com); the
          // backend enforces the authoritative env-gated host check.
          validationErrors.azureContainerUrl =
            'Expected https://<account>.blob.core.windows.net/<container>[/prefix]';
        }
      }

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setSaving(false);
        return;
      }

      await connectionsApi.updateConnection(connectionId, formData);

      // Refresh parent data then close modal
      onUpdate();
      onClose();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update connection';
      setErrors({ general: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('config.')) {
      const configField = field.replace('config.', '');
      setFormData(prev => ({
        ...prev,
        config: {
          ...prev.config,
          [configField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Common input styles
  const inputBaseClass = "w-full px-3 py-2.5 border rounded text-sm transition-colors bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25";
  const inputNormalClass = `${inputBaseClass} border-slate-300 dark:border-slate-600`;
  const inputErrorClass = `${inputBaseClass} border-danger-500`;
  const inputDisabledClass = "w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded text-sm bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed";
  const labelClass = "block mb-1 font-medium text-slate-900 dark:text-slate-100 text-sm";
  const errorTextClass = "block mt-1 text-xs text-danger-500";
  const smallTextClass = "block mt-1 text-xs text-slate-500 dark:text-slate-400";
  const formSectionClass = "border border-slate-200 dark:border-slate-600 rounded-lg p-5 bg-slate-50 dark:bg-slate-700/50";
  const formSectionTitleClass = "m-0 mb-4 text-slate-900 dark:text-slate-100 text-base font-semibold";

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex justify-center items-center z-[1000]">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg relative p-5">
          <div className="text-slate-600 dark:text-slate-300">Loading connection details...</div>
        </div>
      </div>
    );
  }

  if (!connection) {
    return (
      <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex justify-center items-center z-[1000]">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg relative p-5">
          <div className="px-4 py-3 bg-danger-500/10 text-danger-500 border border-danger-500 rounded mb-4">
            Connection not found
          </div>
          <button
            onClick={onClose}
            className="bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 border-none px-5 py-2.5 rounded cursor-pointer text-sm transition-colors hover:bg-slate-300 dark:hover:bg-slate-500"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex justify-center items-center z-[1100]" onClick={(e) => e.stopPropagation()}>
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg relative w-[90%] max-w-[800px] max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-5 pt-5 mb-5">
          <h2 className="m-0 text-slate-900 dark:text-slate-100">Edit Connection: {connection.name}</h2>
          <button
            className="bg-transparent border-none text-2xl cursor-pointer text-slate-500 dark:text-slate-400 p-0 w-[30px] h-[30px] flex items-center justify-center hover:text-slate-900 dark:hover:text-slate-100"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 px-5 pb-5">
          {errors.general && (
            <div className="px-4 py-3 bg-danger-500/10 text-danger-500 border border-danger-500 rounded">
              {errors.general}
            </div>
          )}

          <div className={formSectionClass}>
            <h3 className={formSectionTitleClass}>Basic Information</h3>

            <div className="mb-4">
              <label htmlFor="name" className={labelClass}>Connection Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter connection name"
                className={errors.name ? inputErrorClass : inputNormalClass}
              />
              {errors.name && <span className={errorTextClass}>{errors.name}</span>}
            </div>

            <div className="mb-4">
              <label className={labelClass}>Partner</label>
              <input
                type="text"
                value={connection.partnerName}
                disabled
                className={inputDisabledClass}
              />
              <small className={smallTextClass}>Partner cannot be changed after creation</small>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="mb-4">
                <label className={labelClass}>Hosting Type</label>
                <input
                  type="text"
                  value={connection.hostingType}
                  disabled
                  className={inputDisabledClass}
                />
                <small className={smallTextClass}>Hosting type cannot be changed</small>
              </div>

              <div className="mb-4">
                <label className={labelClass}>Transfer Method</label>
                <input
                  type="text"
                  value={connection.transferMethod}
                  disabled
                  className={inputDisabledClass}
                />
                <small className={smallTextClass}>Transfer method cannot be changed</small>
              </div>
            </div>
          </div>

          {/* THUB-220: Azure Blob Storage Configuration Section */}
          {connection.transferMethod === 'azure_blob' && (
            <div className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Azure Blob Storage Configuration</h3>

              <div className="mb-4">
                <label htmlFor="azureContainerUrl" className={labelClass}>Container URL *</label>
                <input
                  id="azureContainerUrl"
                  type="url"
                  value={formData.config?.azureContainerUrl || ''}
                  /* THUB-287: pasted alongside the SAS, same clipboard hazard. */
                  onChange={(e) => handleInputChange('config.azureContainerUrl', e.target.value.trim())}
                  placeholder="https://account.blob.core.windows.net/container"
                  className={errors.azureContainerUrl ? inputErrorClass : inputNormalClass}
                />
                {errors.azureContainerUrl && <span className={errorTextClass}>{errors.azureContainerUrl}</span>}
                <small className={smallTextClass}>https://&lt;account&gt;.blob.core.windows.net/&lt;container&gt;</small>
              </div>

              {/* THUB-220: no blob-path field here by design -- the path lives on the task
                  (Destination/Source Directory), as it does for SFTP and HTTP. */}

              <div className="mb-4">
                <label htmlFor="sasToken" className={labelClass}>SAS Token</label>
                {/* THUB-287: trim on CHANGE. This is the field the PRD Afficiency incident came
                    through — a rotated SAS pasted with two trailing spaces stored 139 bytes for
                    a 137-byte token. Because the input is type="password" the whitespace is
                    invisible, and because `sig` is last in a SAS the spaces are re-encoded onto
                    the signature, so Azure returns 403 AuthenticationFailed and the error points
                    at the credential rather than the paste. Trimming here also keeps the
                    "blank means keep the stored token" check honest: a whitespace-only paste now
                    reads as blank instead of silently overwriting a working secret with spaces. */}
                <input
                  id="sasToken"
                  type="password"
                  autoComplete="new-password"
                  value={formData.config?.sasToken || ''}
                  onChange={(e) => handleInputChange('config.sasToken', e.target.value.trim())}
                  placeholder="Leave blank to keep current"
                  className={inputNormalClass}
                />
                <small className={smallTextClass}>
                  Write-only. Leave blank to keep the stored token; enter a new SAS to rotate it.
                </small>
              </div>
            </div>
          )}

          {/* T026: HTTP/API Configuration Section */}
          {connection.transferMethod === 'api' && (
            <div className={formSectionClass}>
              <h3 className={formSectionTitleClass}>HTTP/API Configuration</h3>

              <div className="mb-4">
                <label htmlFor="baseUrl" className={labelClass}>Base URL *</label>
                <input
                  id="baseUrl"
                  type="url"
                  value={formData.config?.baseUrl || ''}
                  onChange={(e) => handleInputChange('config.baseUrl', e.target.value)}
                  placeholder="https://api.partner.com"
                  className={errors.baseUrl ? inputErrorClass : inputNormalClass}
                />
                {errors.baseUrl && <span className={errorTextClass}>{errors.baseUrl}</span>}
                <small className={smallTextClass}>Must be a valid HTTPS URL</small>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="mb-4">
                  <label htmlFor="httpMethod" className={labelClass}>HTTP Method *</label>
                  <select
                    id="httpMethod"
                    value={formData.config?.httpMethod || 'POST'}
                    onChange={(e) => handleInputChange('config.httpMethod', e.target.value)}
                    className={inputNormalClass}
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="httpContentType" className={labelClass}>Content Type *</label>
                  <select
                    id="httpContentType"
                    value={formData.config?.httpContentType || 'multipart/form-data'}
                    onChange={(e) => handleInputChange('config.httpContentType', e.target.value)}
                    className={inputNormalClass}
                  >
                    <option value="multipart/form-data">multipart/form-data</option>
                    <option value="application/octet-stream">application/octet-stream</option>
                    <option value="application/json">application/json</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="httpTimeout" className={labelClass}>Timeout (seconds) *</label>
                  <input
                    id="httpTimeout"
                    type="number"
                    value={formData.config?.httpTimeout || 30}
                    onChange={(e) => handleInputChange('config.httpTimeout', parseInt(e.target.value))}
                    min="1"
                    max="120"
                    className={inputNormalClass}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="httpAuthType" className={labelClass}>Authentication Type *</label>
                <select
                  id="httpAuthType"
                  value={formData.config?.httpAuthType || 'none'}
                  onChange={(e) => handleInputChange('config.httpAuthType', e.target.value as HttpAuthType)}
                  className={inputNormalClass}
                >
                  <option value="none">None</option>
                  <option value="basic">Basic Auth</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="oauth2_cc">OAuth2 Client Credentials</option>
                  <option value="oauth2_custom">OAuth2 Custom</option>
                </select>
              </div>

              {/* Basic Auth fields */}
              {formData.config?.httpAuthType === 'basic' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label htmlFor="username" className={labelClass}>Username *</label>
                    <input
                      id="username"
                      type="text"
                      value={formData.config?.username || ''}
                      onChange={(e) => handleInputChange('config.username', e.target.value)}
                      placeholder="Leave blank to keep current"
                      className={errors.username ? inputErrorClass : inputNormalClass}
                    />
                    {errors.username && <span className={errorTextClass}>{errors.username}</span>}
                    <small className={smallTextClass}>Leave blank to keep current username</small>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="password" className={labelClass}>Password</label>
                    <input
                      id="password"
                      type="password"
                      value={formData.config?.password || ''}
                      onChange={(e) => handleInputChange('config.password', e.target.value)}
                      placeholder="Leave blank to keep current"
                      className={inputNormalClass}
                    />
                    <small className={smallTextClass}>Leave blank to keep current password</small>
                  </div>
                </div>
              )}

              {/* Bearer Token field */}
              {formData.config?.httpAuthType === 'bearer' && (
                <div className="mb-4">
                  <label htmlFor="bearerToken" className={labelClass}>Bearer Token</label>
                  <input
                    id="bearerToken"
                    type="password"
                    value={formData.config?.bearerToken || ''}
                    onChange={(e) => handleInputChange('config.bearerToken', e.target.value)}
                    placeholder="Leave blank to keep current"
                    className={inputNormalClass}
                  />
                  <small className={smallTextClass}>Leave blank to keep current token</small>
                </div>
              )}

              {/* OAuth2 Client Credentials fields */}
              {(formData.config?.httpAuthType === 'oauth2_cc' || formData.config?.httpAuthType === 'oauth2_custom') && (
                <div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="mb-4">
                      <label htmlFor="oauth2ClientId" className={labelClass}>Client ID *</label>
                      <input
                        id="oauth2ClientId"
                        type="text"
                        value={formData.config?.oauth2ClientId || ''}
                        onChange={(e) => handleInputChange('config.oauth2ClientId', e.target.value)}
                        placeholder="Enter client ID"
                        className={errors.oauth2ClientId ? inputErrorClass : inputNormalClass}
                      />
                      {errors.oauth2ClientId && <span className={errorTextClass}>{errors.oauth2ClientId}</span>}
                    </div>
                    <div className="mb-4">
                      <label htmlFor="oauth2ClientSecret" className={labelClass}>Client Secret</label>
                      <input
                        id="oauth2ClientSecret"
                        type="password"
                        value={formData.config?.oauth2ClientSecret || ''}
                        onChange={(e) => handleInputChange('config.oauth2ClientSecret', e.target.value)}
                        placeholder="Leave blank to keep current"
                        className={inputNormalClass}
                      />
                      <small className={smallTextClass}>Leave blank to keep current secret</small>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="oauth2TokenEndpoint" className={labelClass}>Token Endpoint *</label>
                    <input
                      id="oauth2TokenEndpoint"
                      type="url"
                      value={formData.config?.oauth2TokenEndpoint || ''}
                      onChange={(e) => handleInputChange('config.oauth2TokenEndpoint', e.target.value)}
                      placeholder="https://auth.partner.com/oauth/token"
                      className={errors.oauth2TokenEndpoint ? inputErrorClass : inputNormalClass}
                    />
                    {errors.oauth2TokenEndpoint && <span className={errorTextClass}>{errors.oauth2TokenEndpoint}</span>}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="oauth2Scope" className={labelClass}>Scope (optional)</label>
                    <input
                      id="oauth2Scope"
                      type="text"
                      value={formData.config?.oauth2Scope || ''}
                      onChange={(e) => handleInputChange('config.oauth2Scope', e.target.value)}
                      placeholder="read:files write:files"
                      className={inputNormalClass}
                    />
                    <small className={smallTextClass}>Space-separated list of OAuth2 scopes</small>
                  </div>

                  {/* OAuth2 Custom Parameters (only for oauth2_custom) */}
                  {formData.config?.httpAuthType === 'oauth2_custom' && (
                    <div className="mb-4">
                      <label className={labelClass}>Custom OAuth2 Parameters</label>
                      {Object.entries(customParams).map(([key, value], idx) => (
                        <div key={idx} className="flex gap-2 mb-2">
                          <input
                            placeholder="Key"
                            value={key}
                            onChange={(e) => {
                              const newParams = { ...customParams };
                              delete newParams[key];
                              newParams[e.target.value] = value;
                              setCustomParams(newParams);
                              handleInputChange('config.oauth2CustomParams', newParams);
                            }}
                            className={inputNormalClass}
                          />
                          <input
                            placeholder="Value"
                            value={value}
                            onChange={(e) => {
                              const newParams = { ...customParams, [key]: e.target.value };
                              setCustomParams(newParams);
                              handleInputChange('config.oauth2CustomParams', newParams);
                            }}
                            className={inputNormalClass}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newParams = { ...customParams };
                              delete newParams[key];
                              setCustomParams(newParams);
                              handleInputChange('config.oauth2CustomParams', newParams);
                            }}
                            className="text-red-500 hover:text-red-700 px-3 text-xl"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newKey = `param${Object.keys(customParams).length + 1}`;
                          const newParams = { ...customParams, [newKey]: '' };
                          setCustomParams(newParams);
                          handleInputChange('config.oauth2CustomParams', newParams);
                        }}
                        className="text-blue-500 hover:text-blue-700 text-sm"
                      >
                        + Add Parameter
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {(connection.hostingType === 'remote' && connection.transferMethod === 'sftp') && (
            <div className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Remote Connection Settings</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="mb-4">
                  <label htmlFor="host" className={labelClass}>Host *</label>
                  <input
                    id="host"
                    type="text"
                    value={formData.config?.host || ''}
                    onChange={(e) => handleInputChange('config.host', e.target.value)}
                    placeholder="partner.example.com"
                    className={errors.host ? inputErrorClass : inputNormalClass}
                  />
                  {errors.host && <span className={errorTextClass}>{errors.host}</span>}
                </div>

                <div className="mb-4">
                  <label htmlFor="port" className={labelClass}>Port *</label>
                  <input
                    id="port"
                    type="number"
                    value={formData.config?.port || 22}
                    onChange={(e) => handleInputChange('config.port', parseInt(e.target.value))}
                    min="1"
                    max="65535"
                    className={errors.port ? inputErrorClass : inputNormalClass}
                  />
                  {errors.port && <span className={errorTextClass}>{errors.port}</span>}
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="username" className={labelClass}>Username *</label>
                <input
                  id="username"
                  type="text"
                  value={formData.config?.username || ''}
                  onChange={(e) => handleInputChange('config.username', e.target.value)}
                  placeholder="Enter username"
                  className={errors.username ? inputErrorClass : inputNormalClass}
                />
                {errors.username && <span className={errorTextClass}>{errors.username}</span>}
              </div>

              <div className="mb-4">
                <label className={labelClass}>Authentication Method</label>
                <input
                  type="text"
                  value={connection.config.authMethod || 'unknown'}
                  disabled
                  className={inputDisabledClass}
                />
                <small className={smallTextClass}>Authentication method cannot be changed</small>
              </div>

              {connection.config.authMethod === 'password' && (
                <div className="mb-4">
                  <label htmlFor="password" className={labelClass}>Password</label>
                  <input
                    id="password"
                    type="password"
                    value={formData.config?.password || ''}
                    onChange={(e) => handleInputChange('config.password', e.target.value)}
                    placeholder="Enter new password (leave blank to keep current)"
                    className={inputNormalClass}
                  />
                  <small className={smallTextClass}>Leave blank to keep the current password</small>
                </div>
              )}

              {connection.config.authMethod === 'key' && (
                <div className="mb-4">
                  <label htmlFor="privateKeyContent" className={labelClass}>Private Key</label>
                  <textarea
                    id="privateKeyContent"
                    value={formData.config?.private_key_content || ''}
                    onChange={(e) => handleInputChange('config.private_key_content', e.target.value)}
                    placeholder="Paste new RSA or ECDSA private key (leave blank to keep current)"
                    rows={4}
                    className={inputNormalClass}
                  />
                  <small className={smallTextClass}>Leave blank to keep the current private key</small>
                </div>
              )}
            </div>
          )}

          {(connection.hostingType === 'hosted' && connection.transferMethod === 'sftp') && (
            <div className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Hosted Connection Settings</h3>

              <div className="mb-4">
                <label htmlFor="publicKeyContent" className={labelClass}>Partner's SSH Public Key (optional)</label>
                <textarea
                  id="publicKeyContent"
                  value={formData.config?.public_key_content || ''}
                  onChange={(e) => handleInputChange('config.public_key_content', e.target.value)}
                  placeholder="ssh-rsa AAAAB3NzaC1yc2E..."
                  rows={4}
                  className={errors.publicKeyContent ? inputErrorClass : inputNormalClass}
                />
                {errors.publicKeyContent && <span className={errorTextClass}>{errors.publicKeyContent}</span>}
                <small className={smallTextClass}>
                  Paste the partner's SSH public key (ssh-rsa, ssh-ed25519, or ecdsa-sha2-nistp*)
                </small>
              </div>

              <div className="mb-4">
                <label htmlFor="allowedIpRanges" className={labelClass}>Allowed IP Ranges (optional)</label>
                <input
                  id="allowedIpRanges"
                  type="text"
                  value={formData.config?.allowed_ip_ranges?.join(', ') || ''}
                  onChange={(e) => handleInputChange('config.allowed_ip_ranges', e.target.value.split(',').map(ip => ip.trim()).filter(ip => ip))}
                  placeholder="192.168.1.0/24, 10.0.0.0/8"
                  className={inputNormalClass}
                />
              </div>
            </div>
          )}

          {/* Connection Test Panel */}
          <div className={formSectionClass}>
            <PreCreationTestPanel
              connectionType={connection.hostingType === 'hosted' ? 'inbound' : 'outbound'}
              transferMethod={connection.transferMethod as TransferMethod}
              config={formData.config || connection.config}
              connectorArn={connection.transferFamilyConnectorArn}
              userArn={connection.transferFamilyUserArn}
              isEditMode={true}
            />
          </div>

          <div className={formSectionClass}>
            <h3 className={formSectionTitleClass}>Advanced Settings</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="mb-4">
                <label htmlFor="timeout" className={labelClass}>Timeout (seconds)</label>
                <input
                  id="timeout"
                  type="number"
                  value={formData.config?.timeout || 30}
                  onChange={(e) => handleInputChange('config.timeout', parseInt(e.target.value))}
                  min="5"
                  max="300"
                  className={inputNormalClass}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="retryAttempts" className={labelClass}>Retry Attempts</label>
                <input
                  id="retryAttempts"
                  type="number"
                  value={formData.config?.retry_attempts || 3}
                  onChange={(e) => handleInputChange('config.retry_attempts', parseInt(e.target.value))}
                  min="0"
                  max="10"
                  className={inputNormalClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center">
                <label className="flex items-center mb-0 cursor-pointer text-slate-900 dark:text-slate-100">
                  <input
                    type="checkbox"
                    checked={formData.config?.enable_encryption || false}
                    onChange={(e) => handleInputChange('config.enable_encryption', e.target.checked)}
                    className="w-auto mr-2 accent-primary-600"
                  />
                  Enable Encryption
                </label>
              </div>

              <div className="flex items-center">
                <label className="flex items-center mb-0 cursor-pointer text-slate-900 dark:text-slate-100">
                  <input
                    type="checkbox"
                    checked={formData.config?.enable_compression || false}
                    onChange={(e) => handleInputChange('config.enable_compression', e.target.checked)}
                    className="w-auto mr-2 accent-primary-600"
                  />
                  Enable Compression
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2.5 pt-5 border-t border-slate-200 dark:border-slate-600">
            <button
              type="button"
              onClick={onClose}
              className="bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 border-none px-5 py-2.5 rounded cursor-pointer text-sm transition-colors hover:bg-slate-300 dark:hover:bg-slate-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 text-white border-none px-5 py-2.5 rounded cursor-pointer text-sm transition-colors hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditConnection;